import { emptyCompany } from '@macs-frontend/features/shared/models/company.model';
import { ProjectsCollectionPermissions } from '@macs-frontend/features/shared/models/project';
import { RoleType } from '@macs-frontend/features/shared/models/role';
import {
  Company,
  CompanyPublic,
  Currency,
  GeoCity,
  GeoCountry,
  Language,
  MdNotification,
  negativeNumberMask,
  Pages,
  Role,
  Timezone,
  User,
} from '@macs-frontend/web-macs/shared';
import { Permission } from '@macs-gql';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Action, createReducer, Store } from 'macs-ngrx-actions';
import {
  defaultCurrency,
  defaultDateFormat,
  defaultTimezoneOffset,
} from '../core/utils/consts';
import * as metaActions from '../features/shared/actions/meta.actions';

export interface MetaState {
  activePage: Pages;
  currencies: Currency[];
  filteredCurrencies: Currency[];
  timezones: Timezone[];
  languages: Language[];
  countries: GeoCountry[];
  cities: GeoCity[];
  user: User;
  roleUnits: Role[];
  currentCompany: Company;
  currentCompanyPublic: CompanyPublic;
  defaultCurrency: Currency;
  notification: MdNotification;
  projectsPermissions: ProjectsCollectionPermissions;
}

export const getMetaState = createFeatureSelector<MetaState>('meta');

export const getNotification = createSelector(getMetaState, state => state.notification);

export const getActivePage = createSelector(getMetaState, state => state.activePage);

export const getCurrencies = createSelector(getMetaState, state => state.currencies);

export const getLanguages = createSelector(getMetaState, state => state.languages);

export const getTimezones = createSelector(getMetaState, state => state.timezones);

export const getCountries = createSelector(getMetaState, state => state.countries);

export const getCurrentCompany = createSelector(
  getMetaState,
  state => state.currentCompany,
);

export const getCurrentCompanyPublic = createSelector(
  getMetaState,
  state => state.currentCompanyPublic,
);

export const getCurrentCompanyNumberMask = createSelector(getCurrentCompany, company =>
  company
    ? {
        ...negativeNumberMask,
        thousandsSeparator: company.numberFormat.charAt(1),
        radix: company.numberFormat.charAt(5),
      }
    : negativeNumberMask,
);

export type UserPermissionsMap = Record<Permission, boolean> & { loaded: boolean };

export const defaultUserPermissions = Object.freeze({
  loaded: false,
}) as UserPermissionsMap;

export function getUserPermissionsMap(user: User) {
  const userPermissionsSet = new Set<string>();

  user.currentRoleUnits?.forEach(roleUnit => {
    roleUnit.role.permissions.forEach(permission => {
      userPermissionsSet.add(permission);
    });
  });

  const userPermissionsMap: UserPermissionsMap = { loaded: true } as UserPermissionsMap;

  Object.values(Permission).forEach(permission => {
    userPermissionsMap[permission] = userPermissionsSet.has('ROLE_' + permission);
  });
  return userPermissionsMap;
}

export const getUserPermissions = createSelector(getMetaState, state => {
  const user = state.user;
  if (!user) {
    return defaultUserPermissions;
  }

  return getUserPermissionsMap(user);
});

export type CurrentUser = User & { permissions: UserPermissionsMap };
export const getCurrentUser = createSelector(
  getMetaState,
  getUserPermissions,
  (state, permissions): CurrentUser => {
    return {
      ...state.user,
      permissions,
    };
  },
);

export const getUserRoles = createSelector(getCurrentUser, user =>
  Object.fromEntries(
    (user?.currentRoleUnits || []).map(roleUnit => [
      roleUnit.role.code,
      roleUnit.role.code,
    ]),
  ),
);

export const getUserTimezoneOffsetMinutes = createSelector(
  getCurrentUser,
  user => user.timezone?.gmt,
);

export const getCities = createSelector(getMetaState, state => state.cities);

export const getRoleUnits = createSelector(getMetaState, state => state.roleUnits);

export const getCompanyRoles = createSelector(getRoleUnits, roles =>
  roles.filter(role => role.type === RoleType.Company),
);

export const getProjectRoles = createSelector(getRoleUnits, roles =>
  roles.filter(role => role.type === RoleType.Project),
);

export const getRegularUserRoleUnit = createSelector(getRoleUnits, roleUnits =>
  roleUnits.find(u => u.code === 'support'),
);

export const getCurrency = createSelector(getMetaState, state => {
  if (state.currentCompanyPublic && state.currentCompanyPublic.currency) {
    return state.currentCompanyPublic.currency;
  } else if (state.currentCompany && state.currentCompany.currency) {
    return state.currentCompany.currency;
  }
  return defaultCurrency;
});

export const getFilteredCurrencies = createSelector(
  getMetaState,
  state => state.filteredCurrencies,
);

export const getDateFormat = createSelector(getMetaState, state =>
  state.currentCompany && state.currentCompany.dateFormat
    ? state.currentCompany.dateFormat
    : defaultDateFormat,
);

export const getCurrentCompanyTimeOffsetMinutes = createSelector(
  getCurrentCompany,
  (company: Company) => company?.timezone?.gmt,
);

export const getPrimeNgCalendarDateFormat = createSelector(getDateFormat, format =>
  format.replace(/yy/g, 'Y').toLowerCase(),
);

export const getDateFormatForNgZorro = createSelector(
  getDateFormat,
  dateFormat => dateFormat,
);

export const getDefaultCurrency = createSelector(
  getMetaState,
  state => state.defaultCurrency,
);

export const canCreateClientWithoutVat = createSelector(
  getCurrentCompany,
  getUserPermissions,
  (company, userPermissions) => {
    return (
      company.canCreateClientWithoutVat ||
      userPermissions.OBJECTS_CLIENT_CREATE_WITHOUT_VAT_NUMBER
    );
  },
);

@Store<MetaState>({
  // @ts-expect-error
  activePage: null,
  currencies: [],
  timezones: [],
  languages: [],
  countries: [],
  cities: [],
  // @ts-expect-error
  user: null,
  roleUnits: [],
  filteredCurrencies: [],
  currentCompany: emptyCompany,
  defaultCurrency,
  notification: { title: '' },
  projectsPermissions: {},
})
export class MetaStore {
  /*@Action(...formActions)
  formsChanged(state: MetaState, action: Actions<any>) {
  }*/
  @Action(metaActions.ShowNotification)
  showNotification(state: MetaState, action: metaActions.ShowNotification) {
    return {
      ...state,
      notification: action.notification,
    };
  }

  @Action(metaActions.SetActivePage)
  setActivePage(state: MetaState, action: metaActions.SetActivePage) {
    return {
      ...state,
      activePage: action.payload,
    };
  }

  @Action(metaActions.ConstsLoaded)
  constsLoaded(state: MetaState, action: metaActions.ConstsLoaded) {
    return {
      ...state,
      currencies: action.payload.currencies.data,
      filteredCurrencies: action.payload.currencies.data,
      languages: Object.keys(action.payload.languages).map(k => ({
        // @ts-expect-error
        name: action.payload.languages[k],
        code: k,
      })),
      timezones: action.payload.timezones.data,
    };
  }

  @Action(metaActions.LocationConstsLoaded)
  locationConstsLoaded(state: MetaState, action: metaActions.LocationConstsLoaded) {
    return {
      ...state,
      countries: action.payload.countries.data,
    };
  }

  @Action(metaActions.CitiesLoaded)
  citiesLoaded(state: MetaState, action: metaActions.CitiesLoaded) {
    return {
      ...state,
      cities: action.payload.cities.data,
    };
  }

  @Action(metaActions.CurrentCompanyLoaded)
  currentCompanyLoaded(state: MetaState, action: metaActions.CurrentCompanyLoaded) {
    return {
      ...state,
      currentCompany: {
        ...action.payload,
        startWeek: +action.payload.startWeek,
      },
    };
  }

  @Action(metaActions.CurrentCompanyPublicLoaded)
  currentCompanyPublicLoaded(
    state: MetaState,
    action: metaActions.CurrentCompanyPublicLoaded,
  ) {
    return {
      ...state,
      currentCompanyPublic: {
        ...action.payload,
        // startWeek: +action.payload.startWeek,
      },
    };
  }

  @Action(metaActions.UserLoaded)
  userLoaded(state: MetaState, action: metaActions.UserLoaded) {
    return {
      ...state,
      user: action.payload.data,
    };
  }

  @Action(metaActions.RoleUnitsLoaded)
  roleUnitsLoaded(state: MetaState, action: metaActions.RoleUnitsLoaded) {
    return {
      ...state,
      roleUnits: action.payload.data,
    };
  }

  @Action(metaActions.RoleUnitUpdated)
  roleUnitUpdated(state: MetaState, action: metaActions.RoleUnitUpdated) {
    return {
      ...state,
      roleUnits: state.roleUnits.map(u => {
        if (u.uuid === action.payload.uuid) {
          return action.payload;
        } else {
          return u;
        }
      }),
    };
  }

  @Action(metaActions.FilterCurrencies)
  getFilteredCurrencies(state: MetaState, action: metaActions.FilterCurrencies) {
    const searchValue = action.payload.toLowerCase();
    return {
      ...state,
      filteredCurrencies: state.currencies.filter(
        currency =>
          currency.name.toLowerCase().indexOf(searchValue) > -1 ||
          // @ts-expect-error
          currency.sign.toLowerCase().indexOf(searchValue) > -1 ||
          currency.code.toLowerCase().indexOf(searchValue) > -1,
      ),
    };
  }
  @Action(metaActions.DefaultCurrencyLoaded)
  defaultCurrencyLoaded(state: MetaState, action: metaActions.DefaultCurrencyLoaded) {
    return {
      ...state,
      defaultCurrency: action.payload,
    };
  }
}

export function metaReducer(state: any, action: any) {
  return createReducer(MetaStore)(state, action);
}
